import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FiUsers, FiPlus, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import CustomTable, { TableColumn } from '../../components/table/table';
import { User, UserGroup, CreateUserGroupRequest, UpdateUserGroupRequest } from '../../shared/types/user';
import { useToast } from '../../components/toast/toastProvider';
import UserGroupsService from '../../services/userGroupsService';
import CreateUserGroupModal from './components/CreateUserGroupModal';
import EditUserGroupModal from './components/EditUserGroupModal';
import UserGroupDetailsModal from './components/UserGroupDetailsModal';
import './user-groups.css';

const UserGroups: React.FC = () => {
  const { addToast } = useToast();
  
  // State
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any | null>(null);
  const [viewingGroup, setViewingGroup] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserGroups = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsResponse, usersResponse] = await Promise.all([
        UserGroupsService.getUserGroups(),
        UserGroupsService.getUsers(),
      ]);
      setUserGroups(groupsResponse.groups || []);
      setAvailableUsers(usersResponse.users || []);
    } catch (error) {
      console.error('Failed to load user groups:', error);
      addToast({
        type: 'error',
        title: 'Failed to Load Groups',
        message: 'Could not fetch user groups',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Load groups from API
  useEffect(() => {
    loadUserGroups();
  }, [loadUserGroups]);

  // Table columns
  const columns = useMemo<TableColumn<UserGroup>[]>(() => [
    {
      accessor: 'name',
      header: 'Group Name',
      enableSorting: true,
      cellRenderer: (value, row) => (
        <div className="group-name-cell">
          <div className="group-name">{value}</div>
          {row.description && (
            <div className="group-description">{row.description}</div>
          )}
        </div>
      )
    },
    {
      accessor: 'members',
      header: 'Members',
      enableSorting: true,
      cellRenderer: (members) => (
        <div className="members-cell">
          <span className="member-count">{members.length} member{members.length !== 1 ? 's' : ''}</span>
          <div className="member-avatars">
            {members.slice(0, 3).map((member: User, index: number) => (
              <div key={member.id} className="member-avatar" title={`${member.firstName} ${member.lastName}`}>
                {member.firstName[0]}{member.lastName[0]}
              </div>
            ))}
            {members.length > 3 && (
              <div className="member-avatar more">+{members.length - 3}</div>
            )}
          </div>
        </div>
      )
    },
    {
      accessor: 'permissions',
      header: 'Permissions',
      enableSorting: false,
      cellRenderer: (permissions) => (
        <div className="permissions-cell">
          <span className="permission-count">{permissions.length} permission{permissions.length !== 1 ? 's' : ''}</span>
          <div className="permission-tags">
            {permissions.slice(0, 2).map((permission: string, index: number) => (
              <span key={index} className="permission-tag">
                {permission.replace(/_/g, ' ')}
              </span>
            ))}
            {permissions.length > 2 && (
              <span className="permission-tag more">+{permissions.length - 2}</span>
            )}
          </div>
        </div>
      )
    },
    {
      accessor: 'isActive',
      header: 'Status',
      enableSorting: true,
      cellRenderer: (isActive) => (
        <div className={`status-cell ${isActive ? 'active' : 'inactive'}`}>
          {isActive ? (
            <>
              <FiEye size={14} />
              <span>Active</span>
            </>
          ) : (
            <>
              <FiEyeOff size={14} />
              <span>Inactive</span>
            </>
          )}
        </div>
      )
    },
    {
      accessor: 'createdAt',
      header: 'Created',
      enableSorting: true,
      cellRenderer: (date) => new Date(date).toLocaleDateString()
    }
  ], []);

  // Handlers
  const handleCreateGroup = useCallback(async (data: CreateUserGroupRequest) => {
    setLoading(true);
    try {
      const newGroup = await UserGroupsService.createUserGroup({
        name: data.name,
        description: data.description,
        permissions: data.permissions,
        members: data.memberIds as any
      });
      
      setUserGroups(prev => [...prev, newGroup as any]);
      setShowCreateModal(false);
      
      addToast({
        type: 'success',
        title: 'Group Created',
        message: `User group "${data.name}" has been created successfully.`,
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to create group:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create user group. Please try again.',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const handleEditGroup = useCallback(async (data: UpdateUserGroupRequest) => {
    if (!editingGroup) return;
    
    setLoading(true);
    try {
      const updateData: any = {
        name: data.name || editingGroup.name,
        description: data.description !== undefined ? data.description : editingGroup.description,
        permissions: data.permissions || editingGroup.permissions
      };
      
      const updatedGroup = await UserGroupsService.updateUserGroup(editingGroup.id, updateData);
      
      setUserGroups(prev => prev.map(group => 
        group.id === editingGroup.id ? (updatedGroup as any) : group
      ));
      setShowEditModal(false);
      setEditingGroup(null);
      
      addToast({
        type: 'success',
        title: 'Group Updated',
        message: `User group "${updatedGroup.name}" has been updated successfully.`,
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to update group:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update user group. Please try again.',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [editingGroup, addToast]);

  const handleDeleteGroup = useCallback(async (groupId: string) => {
    if (!window.confirm('Are you sure you want to delete this user group?')) return;
    
    setLoading(true);
    try {
      const groupToDelete = userGroups.find(group => group.id === groupId);
      await UserGroupsService.deleteUserGroup(groupId);
      
      setUserGroups(prev => prev.filter(group => group.id !== groupId));
      
      addToast({
        type: 'success',
        title: 'Group Deleted',
        message: `User group "${groupToDelete?.name}" has been deleted successfully.`,
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to delete group:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete user group. Please try again.',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [userGroups, addToast]);

  const handleToggleGroupStatus = useCallback(async (groupId: string) => {
    const group = userGroups.find(g => g.id === groupId);
    if (!group) return;
    
    setLoading(true);
    try {
      const updateData: any = {
        isActive: !group.isActive
      };
      
      const updatedGroup = await UserGroupsService.updateUserGroup(groupId, updateData);
      
      setUserGroups(prev => prev.map(g => 
        g.id === groupId ? (updatedGroup as any) : g
      ));
      
      addToast({
        type: 'success',
        title: 'Status Updated',
        message: `User group "${group.name}" has been ${group.isActive ? 'deactivated' : 'activated'}.`,
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update group status. Please try again.',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [userGroups, addToast]);

  const handleRowClick = useCallback((group: UserGroup) => {
    setViewingGroup(group);
    setShowDetailsModal(true);
  }, []);

  const handleSelectionChange = useCallback((selected: UserGroup[]) => {
    setSelectedGroups(selected);
  }, []);

  return (
    <div className="user-groups-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <FiUsers size={32} />
          </div>
          <div className="header-title">
            <h1>User Groups</h1>
            <p className="header-description">
              Manage user groups and assign permissions to organize your team members.
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
          >
            <FiPlus size={16} />
            Create Group
          </button>
        </div>
      </div>

      <div className="page-content">
        <CustomTable
          data={userGroups}
          columns={columns}
          enableSorting={true}
          enableFiltering={true}
          enablePagination={true}
          enableRowSelection={true}
          onRowClick={handleRowClick}
          onSelectionChange={handleSelectionChange}
          pageSizeOptions={[10, 20, 50]}
        />
      </div>

      {/* Action buttons for selected groups */}
      {selectedGroups.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-actions-content">
            <span className="selected-count">
              {selectedGroups.length} group{selectedGroups.length !== 1 ? 's' : ''} selected
            </span>
            <div className="bulk-actions-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  selectedGroups.forEach(group => handleToggleGroupStatus(group.id));
                  setSelectedGroups([]);
                }}
                disabled={loading}
              >
                <FiEye size={16} />
                Toggle Status
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  selectedGroups.forEach(group => handleDeleteGroup(group.id));
                  setSelectedGroups([]);
                }}
                disabled={loading}
              >
                <FiTrash2 size={16} />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserGroupModal
          users={availableUsers}
          onSave={handleCreateGroup}
          onCancel={() => setShowCreateModal(false)}
          loading={loading}
        />
      )}

      {showEditModal && editingGroup && (
        <EditUserGroupModal
          group={editingGroup}
          users={availableUsers}
          onSave={handleEditGroup}
          onCancel={() => {
            setShowEditModal(false);
            setEditingGroup(null);
          }}
          loading={loading}
        />
      )}

      {showDetailsModal && viewingGroup && (
        <UserGroupDetailsModal
          group={viewingGroup}
          onEdit={() => {
            setShowDetailsModal(false);
            setViewingGroup(null);
            setEditingGroup(viewingGroup);
            setShowEditModal(true);
          }}
          onDelete={() => {
            setShowDetailsModal(false);
            setViewingGroup(null);
            handleDeleteGroup(viewingGroup.id);
          }}
          onToggleStatus={() => {
            setShowDetailsModal(false);
            setViewingGroup(null);
            handleToggleGroupStatus(viewingGroup.id);
          }}
          onClose={() => {
            setShowDetailsModal(false);
            setViewingGroup(null);
          }}
        />
      )}
    </div>
  );
};

export default UserGroups;
